import { useEffect, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ToastContainer } from "@/components/ui/Toast";
import { useToast, type UseToastResult } from "@/hooks/useToast";
import { ApiError } from "@/lib/api";
import { brokerApi } from "@/lib/brokerApi";
import { fyersApi } from "@/lib/fyersApi";
import { buildFyersAccount, buildSingleAccount, type BrokerAccount } from "@/lib/brokerAccounts";
import { strategiesApi } from "@/lib/api";
import { DisconnectModal } from "./DisconnectModal";
import { BrokerCard, FuturePlaceholders, PageHeader, StrategyMapping } from "./BrokerCards";
import { DhanSection } from "./DhanSection";

const STATUS_KEY = ["broker", "status"] as const;
const POSITIONS_KEY = ["broker", "positions"] as const;
const FYERS_STATUS_KEY = ["broker", "fyers", "status"] as const;
const STRATEGIES_KEY = ["strategies"] as const;
const POLL_MS = 30_000;
const CLOCK_MS = 30_000;

export function BrokerConnectPage(): ReactElement {
  const qc = useQueryClient();
  const toast = useToast();
  const now = useTickingClock();
  useConsumeConnectedParam(toast, qc, "connected", STATUS_KEY);
  useConsumeConnectedParam(toast, qc, "fyers_connected", FYERS_STATUS_KEY);
  const accounts = useAccounts();
  const fyersAccounts = useFyersAccounts();
  const m = useConnectMutations(toast, qc);
  const [disconnectFor, setDisconnectFor] = useState<BrokerAccount | null>(null);
  function requestDisconnect(account: BrokerAccount): void {
    if (account.openPositionCount > 0) {
      setDisconnectFor(account);
      return;
    }
    m.logoutMut.mutate();
  }
  return (
    <PageContent
      accounts={accounts}
      fyersAccounts={fyersAccounts}
      now={now}
      busy={m.busy}
      logoutPending={m.logoutMut.isPending}
      disconnectFor={disconnectFor}
      onClearDisconnect={() => {
        setDisconnectFor(null);
      }}
      onLogin={() => {
        m.loginMut.mutate();
      }}
      onLogout={() => {
        m.logoutMut.mutate();
      }}
      onRequestDisconnect={requestDisconnect}
      onFyersConnect={() => {
        m.fyersLoginMut.mutate();
      }}
      onFyersDisconnect={() => {
        m.fyersLogoutMut.mutate();
      }}
      toast={toast}
    />
  );
}

function useConnectMutations(
  toast: UseToastResult,
  qc: ReturnType<typeof useQueryClient>,
): {
  loginMut: ReturnType<typeof useLoginMutation>;
  logoutMut: ReturnType<typeof useLogoutMutation>;
  fyersLoginMut: ReturnType<typeof useFyersLoginMutation>;
  fyersLogoutMut: ReturnType<typeof useFyersLogoutMutation>;
  busy: boolean;
} {
  const loginMut = useLoginMutation(toast);
  const logoutMut = useLogoutMutation(toast, qc);
  const fyersLoginMut = useFyersLoginMutation(toast);
  const fyersLogoutMut = useFyersLogoutMutation(toast, qc);
  const busy =
    loginMut.isPending ||
    logoutMut.isPending ||
    fyersLoginMut.isPending ||
    fyersLogoutMut.isPending;
  return { loginMut, logoutMut, fyersLoginMut, fyersLogoutMut, busy };
}

interface PageContentProps {
  accounts: BrokerAccount[];
  fyersAccounts: BrokerAccount[];
  now: Date;
  busy: boolean;
  logoutPending: boolean;
  disconnectFor: BrokerAccount | null;
  onClearDisconnect: () => void;
  onLogin: () => void;
  onLogout: () => void;
  onRequestDisconnect: (a: BrokerAccount) => void;
  onFyersConnect: () => void;
  onFyersDisconnect: () => void;
  toast: UseToastResult;
}

function PageContent(props: Readonly<PageContentProps>): ReactElement {
  const { accounts, fyersAccounts, toast } = props;
  return (
    <main className="mx-auto max-w-5xl px-6 py-6">
      <PageHeader
        onAddAccount={() => {
          toast.push("error", "Multi-account support coming once the backend ships it");
        }}
      />
      <BrokerCards {...props} />
      <FuturePlaceholders />
      <StrategyMapping accounts={[...accounts, ...fyersAccounts]} />
      <DisconnectModal
        account={props.disconnectFor}
        busy={props.logoutPending}
        onCancel={props.onClearDisconnect}
        onConfirmDeferred={() => {
          toast.push("error", "Deferred disconnect coming once the backend ships it");
          props.onClearDisconnect();
        }}
        onConfirmNow={() => {
          props.onLogout();
          props.onClearDisconnect();
        }}
      />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </main>
  );
}

function BrokerCards(props: Readonly<PageContentProps>): ReactElement {
  const { accounts, fyersAccounts, now, busy } = props;
  return (
    <>
      <BrokerCard
        broker="Zerodha"
        subtitle="Kite Connect API"
        icon="ti-building-bank"
        accounts={accounts}
        now={now}
        busy={busy}
        onConnect={props.onLogin}
        onDisconnect={props.onRequestDisconnect}
      />
      <BrokerCard
        broker="Fyers"
        subtitle="Fyers API v3 · 5/50 EMA trend"
        icon="ti-chart-candle"
        accounts={fyersAccounts}
        now={now}
        busy={busy}
        onConnect={props.onFyersConnect}
        onDisconnect={() => {
          props.onFyersDisconnect();
        }}
      />
      <DhanSection now={now} toast={props.toast} />
    </>
  );
}

function useTickingClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, CLOCK_MS);
    return () => {
      clearInterval(id);
    };
  }, []);
  return now;
}

function useAccounts(): BrokerAccount[] {
  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => brokerApi.status(),
    refetchInterval: POLL_MS,
  });
  const connected = status.data?.connected ?? false;
  const positions = useQuery({
    queryKey: POSITIONS_KEY,
    queryFn: () => brokerApi.positions(),
    enabled: connected,
    refetchInterval: connected ? POLL_MS : false,
    retry: false,
  });
  const strategies = useQuery({ queryKey: STRATEGIES_KEY, queryFn: () => strategiesApi.list() });

  if (status.data === undefined) return [];
  const assigned = (strategies.data ?? []).filter((s) => s.broker === "kite").map((s) => s.name);
  const positionList = positions.data?.positions ?? [];
  return [buildSingleAccount(status.data, positionList, assigned)];
}

function useConsumeConnectedParam(
  toast: UseToastResult,
  qc: ReturnType<typeof useQueryClient>,
  paramName: string,
  statusKey: readonly string[],
): void {
  const [params, setParams] = useSearchParams();
  const connectedParam = params.get(paramName);
  useEffect(() => {
    if (connectedParam === "1") {
      toast.push("success", "Connected to broker");
      setParams({}, { replace: true });
      void qc.invalidateQueries({ queryKey: statusKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedParam]);
}

function useFyersAccounts(): BrokerAccount[] {
  const status = useQuery({
    queryKey: FYERS_STATUS_KEY,
    queryFn: () => fyersApi.status(),
    refetchInterval: POLL_MS,
  });
  const strategies = useQuery({ queryKey: STRATEGIES_KEY, queryFn: () => strategiesApi.list() });
  if (status.data === undefined) return [];
  const assigned = (strategies.data ?? []).filter((s) => s.broker === "fyers").map((s) => s.name);
  return [buildFyersAccount(status.data, assigned)];
}

function useFyersLoginMutation(
  toast: UseToastResult,
): ReturnType<typeof useMutation<unknown, unknown, void>> {
  return useMutation({
    mutationFn: () => fyersApi.loginUrl(),
    onSuccess: (data) => {
      window.location.href = data.login_url;
    },
    onError: (err) => {
      const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
      toast.push("error", `Fyers login failed: ${reason}`);
    },
  });
}

function useFyersLogoutMutation(
  toast: UseToastResult,
  qc: ReturnType<typeof useQueryClient>,
): ReturnType<typeof useMutation<unknown, unknown, void>> {
  return useMutation({
    mutationFn: () => fyersApi.logout(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FYERS_STATUS_KEY });
      toast.push("success", "Disconnected from Fyers");
    },
    onError: () => {
      toast.push("error", "Fyers logout failed");
    },
  });
}

function useLoginMutation(
  toast: UseToastResult,
): ReturnType<typeof useMutation<unknown, unknown, void>> {
  return useMutation({
    mutationFn: () => brokerApi.loginUrl(),
    onSuccess: (data) => {
      window.location.href = data.login_url;
    },
    onError: (err) => {
      const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
      toast.push("error", `Login failed: ${reason}`);
    },
  });
}

function useLogoutMutation(
  toast: UseToastResult,
  qc: ReturnType<typeof useQueryClient>,
): ReturnType<typeof useMutation<unknown, unknown, void>> {
  return useMutation({
    mutationFn: () => brokerApi.logout(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STATUS_KEY });
      toast.push("success", "Disconnected from broker");
    },
    onError: () => {
      toast.push("error", "Logout failed");
    },
  });
}
